import React from "react"
import { useTheme } from "styled-components"
import { Link } from "gatsby"

import { maxWidth } from "utils"

import {
  Container,
  Header,
  AboveTitle,
  TopRight,
  Headline,
  Subheadline,
  MediaContainer,
  MediaIcon,
  BottomSign,
  ImageRegular,
  ImageCropped,
} from "./styles"

const Card = ({
  variant = "4col",
  topLeftElement,
  topRightElement,
  headline,
  headlineOverride,
  headlineVariant,
  headlineColor,
  subheadline,
  bylineElement,
  mediaIcon,
  bottomRightElement,
  imageUrl,
  imageCrops,
  imageOverride,
  zoomImageOnHover,
  squashImageVertically,
  link,
  localLink,
  lazyLoadImage,
  isGrid,
  elRef,
  dataId,
  video,
  hideOnPageLoad,
  imagePosition = "top",
  urlKey,
  renderImage = true,
  isRailItem,
  mobileHeadlineSize,
  ...props
}) => {
  const theme = useTheme()
  const lazy = typeof lazyLoadImage === "boolean" ? lazyLoadImage : !theme.component?.onTop

  const localLinkOverride = localLink || (link && link.startsWith("/"))
  const LinkComponent = localLinkOverride
    ? (linkComponentProps) => <Link {...linkComponentProps} />
    : (linkComponentProps) => <a {...linkComponentProps} /> // eslint-disable-line
  const linkProps = localLinkOverride
    ? { to: link, title: headline }
    : { href: link, title: headline }

  let imgWLargeDesktop
  let imgHLargeHDesktop
  let imgWDesktop
  let imgHDesktop
  let imgWTablet
  let imgHTablet
  let imgWMobile
  let imgHMobile

  // squashImageVertically gives images ration (1.8 / 1)
  const calculateHeight = (width) => {
    if (squashImageVertically) {
      return Math.round(width / 1.8)
    }
    return Math.round(width * 0.673)
  }

  switch (variant) {
    case "2col":
      imgWLargeDesktop = 865
      imgHLargeHDesktop = calculateHeight(imgWLargeDesktop)
      imgWDesktop = 652
      imgHDesktop = calculateHeight(imgWDesktop)
      imgWTablet = 966
      imgHTablet = calculateHeight(imgWTablet)
      imgWMobile = 726
      imgHMobile = calculateHeight(imgWMobile)
      break
    case "3col":
      imgWLargeDesktop = 414
      imgHLargeHDesktop = calculateHeight(imgWLargeDesktop)
      imgWDesktop = 414
      imgHDesktop = calculateHeight(imgWDesktop)
      imgWTablet = 404
      imgHTablet = calculateHeight(imgWTablet)
      imgWMobile = 440
      imgHMobile = calculateHeight(imgWMobile)
      break
    case "4col":
      imgWLargeDesktop = 614
      imgHLargeHDesktop = calculateHeight(imgWLargeDesktop)
      imgWDesktop = 404
      imgHDesktop = calculateHeight(imgWDesktop)
      imgWTablet = 404
      imgHTablet = calculateHeight(imgWTablet)
      imgWMobile = 440
      imgHMobile = calculateHeight(imgWMobile)
      break
    default:
      imgWLargeDesktop = 310
      imgHLargeHDesktop = calculateHeight(imgWLargeDesktop)
      imgWDesktop = 310
      imgHDesktop = calculateHeight(imgWDesktop)
      imgWTablet = 336
      imgHTablet = calculateHeight(imgWTablet)
      imgWMobile = 460
      imgHMobile = calculateHeight(imgWMobile)
  }

  const imageElement =
    imageOverride ||
    (imageUrl ? (
      <ImageRegular
        variant={variant}
        baseUrl={imageUrl}
        alt={`Read article: ${headline}`}
        images={[
          {
            screenSize: 1200,
            transforms: `c_fill,g_auto,w_${imgWLargeDesktop},h_${imgHLargeHDesktop},q_90,f_auto,fl_lossy`,
          },
          {
            screenSize: 1024,
            transforms: `c_fill,g_auto,w_${imgWDesktop},h_${imgHDesktop},q_90,f_auto,fl_lossy`,
          },
          {
            screenSize: 768,
            transforms: `c_fill,g_auto,w_${imgWTablet},h_${imgHTablet},q_90,f_auto,fl_lossy`,
          },
          {
            transforms: `c_fill,g_auto,w_${imgWMobile},h_${imgHMobile},q_90,f_auto,fl_lossy`,
          },
        ]}
        lazy={lazy}
        isGrid={isGrid}
        imagePosition={imagePosition}
        zoomImageOnHover={zoomImageOnHover}
        squashImageVertically={squashImageVertically}
      />
    ) : imageCrops ? (
      <ImageCropped
        variant={variant}
        imageData={imageCrops}
        alt={`Read article: ${headline}`}
        sizes={[
          {
            query: 1200,
            width: imgWLargeDesktop,
            height: imgHLargeHDesktop,
            transforms: `c_fill,g_auto,w_${imgWLargeDesktop},h_${imgHLargeHDesktop},q_90,f_auto,fl_lossy`,
          },
          {
            query: 1024,
            width: imgWDesktop,
            height: imgHDesktop,
            transforms: `c_fill,g_auto,w_${imgWDesktop},h_${imgHDesktop},q_90,f_auto,fl_lossy`,
          },
          {
            query: 768,
            width: imgWTablet,
            height: imgHTablet,
            transforms: `c_fill,g_auto,w_${imgWTablet},h_${imgHTablet},q_90,f_auto,fl_lossy`,
          },
          {
            query: 0,
            width: imgWMobile,
            height: imgHMobile,
            transforms: `c_fill,g_auto,w_${imgWMobile},h_${imgHMobile},q_90,f_auto,fl_lossy`,
          },
        ]}
        lazy={lazy}
        isGrid={isGrid}
        imagePosition={imagePosition}
        zoomImageOnHover={zoomImageOnHover}
        squashImageVertically={squashImageVertically}
      />
    ) : null)

  const hasBothVideoAndImage = video && imageElement
  const headlineElement = headlineOverride || headline

  const onKeyDown = (e) => {
    if (
      props?.type !== "navigation" ||
      !props.closeButtonRef ||
      !props.closeButtonRef.current ||
      !props?.lastItem
    )
      return
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault()
      props.closeButtonRef.current.focus()
    }
  }

  return (
    <Container
      hideOnPageLoad={hideOnPageLoad}
      data-exp-id={dataId}
      ref={elRef}
      isGrid={isGrid}
      imagePosition={imagePosition}
      tabIndex={-1}
      renderImage={renderImage}
      {...props}
    >
      {renderImage && (
        <LinkComponent
          style={{ width: isGrid || maxWidth.tablet ? "100%" : "50%" }}
          {...linkProps}
          {...(!hasBothVideoAndImage && props?.type !== "navigation" && { tabIndex: "-1" })}
          onKeyDown={onKeyDown}
        >
          <MediaContainer variant={variant} isGrid={isGrid} imagePosition={imagePosition}>
            {hasBothVideoAndImage ? (
              <>
                {imageElement}
                {video}
                {headlineElement}
              </>
            ) : (
              <>
                {video && video}
                {!video && imageElement}
              </>
            )}
            {mediaIcon && <MediaIcon>{mediaIcon}</MediaIcon>}
            {bottomRightElement && imageElement && <BottomSign>{bottomRightElement}</BottomSign>}
          </MediaContainer>
        </LinkComponent>
      )}

      <Header
        className="card-header"
        isGrid={isGrid}
        hasImage={renderImage}
        imagePosition={imagePosition}
      >
        {(topLeftElement || topRightElement) && (
          <AboveTitle hasImage={renderImage}>
            {topLeftElement}
            <TopRight>{topRightElement}</TopRight>
          </AboveTitle>
        )}
        {!hasBothVideoAndImage && headlineElement && (
          <LinkComponent
            {...linkProps}
            tabIndex={(linkProps.to || linkProps.href) && props?.type !== "navigation" ? 0 : -1}
          >
            {headlineOverride && headlineElement}
            {!headlineOverride && (
              <Headline
                variant={headlineVariant}
                headlineColor={headlineColor}
                mobileHeadlineSize={mobileHeadlineSize}
              >
                {headline}
              </Headline>
            )}
          </LinkComponent>
        )}
        <Subheadline dangerouslySetInnerHTML={{ __html: subheadline || "" }} />
        {bylineElement}
      </Header>
    </Container>
  )
}

export default Card
